import { Field, Radio, RadioGroup } from '@headlessui/react';
import { BasicBusinessHoursFragment, BasicPreBookingFragment } from 'drycut-graphql';
import { DateTime } from 'luxon';
import { buttonVariants, Para, Stack } from 'ui-components';
import { getDaySchedule } from './utils';
import { useCheckoutStore } from '../../checkoutStore';

type Props = {
  businessHours: BasicBusinessHoursFragment;
  selectedDate: DateTime;
  onSelect: (date: DateTime) => void;
  preBookings: BasicPreBookingFragment[];
};

export default function SlotSelect({
  selectedDate,
  preBookings,
  onSelect,
  businessHours,
}: Props): React.ReactElement {
  const selectedStore = useCheckoutStore((state) => state.selectedStore);

  const daySchedule = getDaySchedule(selectedDate, businessHours);

  const timeSlots = [];
  let currentTime = daySchedule.start;

  // Interval in minutes before you can book a slot
  const INTERVAL_BEFORE_BOOKING = 45;

  while (currentTime <= daySchedule.end.minus({ minutes: 30 })) {
    timeSlots.push(currentTime);
    currentTime = currentTime.plus({ minutes: 30 });
  }

  const isBooked = (time: DateTime) => {
    return preBookings.some((booking) => {
      const bookingStart = DateTime.fromISO(booking.bookTime).startOf('minute');
      return time.startOf('minute').equals(bookingStart);
    });
  };

  return (
    <RadioGroup
      value={selectedDate.toISO()}
      onChange={(value) => onSelect(DateTime.fromISO(value as string))}
      className="grid grid-cols-2 w-full gap-3"
    >
      {timeSlots.map((time) => {
        const disabled =
          time.diff(DateTime.now(), 'minutes').minutes <
            INTERVAL_BEFORE_BOOKING + Number(selectedStore?.waitingTime) / 60 || isBooked(time);

        const { wrapper, text } = buttonVariants({
          fill: 'ghost',
          disabled,
        });

        return (
          <Field key={time.toISO()} className="flex">
            <Radio
              value={time.toISO()}
              disabled={disabled}
              className={wrapper({
                className:
                  'font-aribau bg-gray-200 hover:bg-primary-200 aria-disabled:hover:bg-gray-200 w-full p-3',
              })}
            >
              <Stack direction="row" className={text({ className: 'text-sm' })}>
                <Para size="sm" weight="bold">
                  {time.toFormat('HH:mm')}
                </Para>
                <span>&ndash;</span>
                <Para size="sm">{time.plus({ minutes: 15 }).toFormat('HH:mm')}</Para>
              </Stack>
            </Radio>
          </Field>
        );
      })}
    </RadioGroup>
  );
}
